import { useMemo, useState } from 'react';
import type { ApiScope, PatCreated } from '@salonomy/types';
import { Button, Card, Field, Modal, Pill, TextInput } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useCreateToken, useRevokeToken } from '../../data/tokens.js';
import { useToast } from '../../lib/toast.js';
import { FINE_SCOPES, isCoveredBySuper, SCOPE_LABEL, SUPER_SCOPES } from './scopes.js';

/**
 * Personal access tokens for the public API (WS6 developer panel, brief item 6). ADR-0006: the raw
 * token is shown ONCE at creation — only a hash is stored — and there is no list endpoint, so this
 * panel can only show tokens minted in THIS session. That is a deliberate honesty point, surfaced in
 * the copy: "We only store a hash. Copy it now — you won't see it again, and you can't list past
 * tokens." Revoke is a typed-confirmation destructive action.
 *
 * Owner/manager only — the parent DeveloperPanel mounts this behind the isManager gate; we don't
 * re-check role here because there is no per-row escalation, only the salon-wide manager rule.
 */
export function TokensPanel() {
  const toast = useToast();
  const create = useCreateToken();
  const revoke = useRevokeToken();

  const [modalOpen, setModalOpen] = useState(false);
  // Tokens minted this session. `revealed` holds the one whose raw value we still show.
  const [minted, setMinted] = useState<PatCreated[]>([]);
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<PatCreated | null>(null);
  const [busy, setBusy] = useState(false);

  async function onCreated(token: PatCreated) {
    setMinted((m) => [token, ...m]);
    setRevealedId(token.id);
    setModalOpen(false);
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Token copied');
    } catch {
      toast.error('Could not copy — select and copy it manually.');
    }
  }

  async function confirmRevoke() {
    if (!toRevoke) return;
    setBusy(true);
    try {
      await revoke.mutateAsync(toRevoke.id);
      setMinted((m) => m.filter((t) => t.id !== toRevoke.id));
      if (revealedId === toRevoke.id) setRevealedId(null);
      toast.success(`Token “${toRevoke.name}” revoked`);
      setToRevoke(null);
    } catch {
      toast.error('Could not revoke this token.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="section-title">Personal access tokens</div>
        <Button size="sm" onClick={() => setModalOpen(true)} disabled={create.isPending}>
          Create token
        </Button>
      </div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        Bearer tokens for the public REST API. We only ever store a hash — a token is shown once
        when you create it, and past tokens can't be listed. Revoke a token to cut its access
        immediately.
      </div>

      {minted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '14px 0 2px' }}>
          No tokens created in this session.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 10 }}>
          {minted.map((t) => (
            <div key={t.id} className="settings-row" style={{ alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                <div
                  style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 0' }}
                  aria-label="scopes"
                >
                  {t.scopes.map((s) => (
                    <Pill key={s} tone="soft" className="mono">
                      {s}
                    </Pill>
                  ))}
                </div>
                {revealedId === t.id ? (
                  <div
                    style={{
                      marginTop: 8,
                      background: 'var(--slm-porcelain)',
                      border: '1px solid var(--slm-border)',
                      borderRadius: 8,
                      padding: '8px 10px',
                    }}
                  >
                    <div className="muted mono" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                      {t.token}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                      <Button size="sm" variant="soft" onClick={() => copy(t.token)}>
                        Copy
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRevealedId(null)}>
                        Hide — I've saved it
                      </Button>
                      <span className="muted" style={{ fontSize: 11.5 }}>
                        You won't see this value again.
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                {t.expiresAt ? (
                  <Pill tone="neutral" className="mono">
                    expires {t.expiresAt.slice(0, 10)}
                  </Pill>
                ) : (
                  <Pill tone="neutral">No expiry</Pill>
                )}
                <Button size="sm" variant="ghost" onClick={() => setToRevoke(t)}>
                  Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen ? (
        <CreateTokenModal
          busy={create.isPending}
          onCancel={() => setModalOpen(false)}
          onSubmit={async (name, scopes, expiresAt) => {
            try {
              const created = await create.mutateAsync({ name, scopes, expiresAt });
              await onCreated(created);
              toast.success('Token created — copy it now');
            } catch {
              toast.error('Could not create the token.');
            }
          }}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(toRevoke)}
        title="Revoke access token"
        confirmLabel="Revoke"
        confirmPhrase={toRevoke?.name}
        busy={busy}
        onConfirm={confirmRevoke}
        onClose={() => setToRevoke(null)}
      >
        <p style={{ margin: 0 }}>
          Revoking <strong>{toRevoke?.name}</strong> immediately stops any integration using it.
          Anything calling the API with this token will start getting 401s. This can't be undone —
          you'd create a new token instead.
        </p>
      </ConfirmModal>
    </Card>
  );
}

/** Create-token form: name, scope picker (super-scopes + fine-grained), optional expiry. */
function CreateTokenModal({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (name: string, scopes: ApiScope[], expiresAt: string | null) => void | Promise<void>;
  onCancel: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<ApiScope>>(new Set<ApiScope>(['read']));
  const [expiry, setExpiry] = useState('');

  const chosen = useMemo<ReadonlySet<string>>(() => selected, [selected]);

  function toggle(scope: ApiScope) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      // Choosing `write` implies `read` for the user's intuition; choosing `read` alone is fine.
      if (scope === 'write' && next.has('write')) next.delete('read');
      return next;
    });
  }

  function submit() {
    if (!name.trim()) {
      toast.error('Give the token a name so you can recognise it later.');
      return;
    }
    if (selected.size === 0) {
      toast.error('Pick at least one scope.');
      return;
    }
    // Drop fine-grained scopes already covered by a chosen super-scope — they're redundant.
    const scopes = [...selected].filter((s) => !isCoveredBySuper(s, chosen));
    const expiresAt = expiry ? new Date(`${expiry}T23:59:59`).toISOString() : null;
    void onSubmit(name.trim(), scopes, expiresAt);
  }

  return (
    <Modal
      open
      onClose={onCancel}
      title="Create access token"
      width={540}
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            Create token
          </Button>
        </>
      }
    >
      <Field label="Name" hint="e.g. “Zapier integration” or “Nightly export script”.">
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Integration name"
        />
      </Field>

      <div className="section-title" style={{ fontSize: 15, margin: '10px 0 4px' }}>
        Scopes
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        Grant the least the integration needs. The two broad scopes cover the fine-grained ones:
        “read” includes every read, “write” includes everything.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {SUPER_SCOPES.map((s) => {
          const on = selected.has(s);
          return (
            <button
              key={s}
              type="button"
              className={`policy${on ? ' policy--active' : ''}`}
              style={{ flex: 1 }}
              onClick={() => toggle(s)}
            >
              <div className="policy__title">
                {SCOPE_LABEL[s]}
                {on ? <Pill tone="cobalt">On</Pill> : null}
              </div>
              <div className="policy__blurb mono">{s}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {FINE_SCOPES.map((s) => {
          const covered = isCoveredBySuper(s, chosen);
          const on = selected.has(s) || covered;
          return (
            <label
              key={s}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12.5,
                padding: '4px 2px',
                opacity: covered ? 0.55 : 1,
              }}
              title={covered ? 'Already covered by a broad scope above' : undefined}
            >
              <input type="checkbox" checked={on} disabled={covered} onChange={() => toggle(s)} />
              <span>{SCOPE_LABEL[s]}</span>
            </label>
          );
        })}
      </div>

      <div className="section-title" style={{ fontSize: 15, margin: '14px 0 4px' }}>
        Expiry
      </div>
      <Field label="Expires on" hint="Optional — leave blank for a token that never expires.">
        <TextInput type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
      </Field>
    </Modal>
  );
}
